import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity, 
  Loader2, 
  Search,
  User,
  Clock,
  LogIn,
  LogOut,
  Building2,
  RefreshCw,
  Users,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronUp,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin_struttura: 'Admin Struttura',
  operatore: 'Operatore',
  cucina: 'Cucina'
};

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-700',
  admin_struttura: 'bg-purple-100 text-purple-700',
  operatore: 'bg-blue-100 text-blue-700',
  cucina: 'bg-orange-100 text-orange-700'
};

// Device type icons
const DeviceIcon = ({ type }) => {
  switch (type) {
    case 'mobile':
      return <Smartphone className="w-4 h-4" />;
    case 'tablet':
      return <Tablet className="w-4 h-4" />;
    default:
      return <Monitor className="w-4 h-4" />;
  }
};

// Format duration in a human-readable way
const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}g ${remainingHours}h`;
};

export const AccessLogsPage = () => {
  const { api, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('online');
  const [structuresData, setStructuresData] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState({ total_online: 0, sessions: [], by_structure: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedStructures, setExpandedStructures] = useState({});

  const fetchAccessLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, onlineRes] = await Promise.all([
        api.get('/access-logs/by-structure'),
        api.get('/access-logs/online')
      ]);
      setStructuresData(logsRes.data);
      setOnlineUsers(onlineRes.data);
      
      // Auto-expand structures with active sessions
      const expanded = {};
      logsRes.data.forEach(s => {
        if (s.active_sessions > 0) {
          expanded[s.structure_id] = true;
        }
      });
      setExpandedStructures(prev => ({ ...prev, ...expanded }));
    } catch (error) {
      console.error('Failed to fetch access logs:', error);
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAccessLogs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAccessLogs, 30000);
    return () => clearInterval(interval);
  }, [fetchAccessLogs]);

  const toggleStructure = (structureId) => {
    setExpandedStructures(prev => ({
      ...prev,
      [structureId]: !prev[structureId]
    }));
  };

  // Filter structures by search
  const filteredStructures = structuresData.filter(s =>
    s.structure_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.logs?.some(log => log.username.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Accesso non autorizzato</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="access-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Log Accessi</h1>
          <p className="text-slate-500">Monitoraggio accessi e sessioni attive</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchAccessLogs}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-green-500 rounded-xl shrink-0">
                <Wifi className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold text-green-700">{onlineUsers.total_online}</p>
                <p className="text-xs sm:text-sm text-green-600 truncate">Online</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-xl shrink-0">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold">{structuresData.length}</p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Strutture</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-xl shrink-0">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold">
                  {structuresData.reduce((acc, s) => acc + (s.total_logs || 0), 0)}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Accessi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-amber-100 rounded-xl shrink-0">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold">
                  {structuresData.filter(s => s.active_sessions > 0).length}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Attive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="online" className="flex items-center gap-2">
            <Wifi className="w-4 h-4" />
            Utenti Online ({onlineUsers.total_online})
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Storico Accessi
          </TabsTrigger>
        </TabsList>

        {/* Tab: Utenti Online */}
        <TabsContent value="online" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
            </div>
          ) : onlineUsers.total_online === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <WifiOff className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Nessun utente online al momento</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {onlineUsers.sessions.map((session) => (
                <Card key={session.id} className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        {/* User Avatar with Online Indicator */}
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                        </div>
                        
                        {/* User Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-lg">{session.username}</p>
                            <Badge className={ROLE_COLORS[session.role] || 'bg-slate-100'}>
                              {ROLE_LABELS[session.role] || session.role}
                            </Badge>
                          </div>
                          {session.structure_name && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {session.structure_name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Session Details */}
                      <div className="text-right space-y-1">
                        <div className="flex items-center gap-2 justify-end text-sm text-slate-600">
                          <Clock className="w-4 h-4" />
                          Online da {formatDuration(session.duration_minutes)}
                        </div>
                        <p className="text-xs text-slate-400">
                          Login: {session.login_at ? format(parseISO(session.login_at), 'HH:mm', { locale: it }) : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Device & Location Info */}
                    <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Device */}
                      <div className="flex items-center gap-2 text-sm">
                        <DeviceIcon type={session.device_type} />
                        <span className="text-slate-600">
                          {session.device_type === 'mobile' ? 'Mobile' : 
                           session.device_type === 'tablet' ? 'Tablet' : 'Desktop'}
                        </span>
                      </div>

                      {/* Browser/OS */}
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">
                          {session.browser || 'N/A'} / {session.os || 'N/A'}
                        </span>
                      </div>

                      {/* IP Address */}
                      <div className="flex items-center gap-2 text-sm">
                        <Wifi className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600 font-mono text-xs">
                          {session.ip_address || 'N/A'}
                        </span>
                      </div>

                      {/* Location */}
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">
                          {session.location ? 
                            `${session.location.city || ''}, ${session.location.country || ''}`.replace(/^, |, $/g, '') || 'N/A'
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Storico Accessi */}
        <TabsContent value="logs" className="mt-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per struttura o utente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
            </div>
          ) : filteredStructures.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Nessun log di accesso trovato</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredStructures.map((structure) => (
                <Card key={structure.structure_id}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleStructure(structure.structure_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-blue-700" />
                        <div>
                          <CardTitle className="text-lg">{structure.structure_name}</CardTitle>
                          <CardDescription>
                            {structure.active_sessions > 0 && (
                              <span className="text-green-600 font-medium">
                                {structure.active_sessions} online • 
                              </span>
                            )}
                            {' '}{structure.total_logs} accessi totali
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {structure.active_sessions > 0 && (
                          <Badge className="bg-green-100 text-green-700">
                            <Wifi className="w-3 h-3 mr-1" />
                            {structure.active_sessions} online
                          </Badge>
                        )}
                        {expandedStructures[structure.structure_id] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {expandedStructures[structure.structure_id] && structure.logs?.length > 0 && (
                    <CardContent className="pt-0">
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {structure.logs.slice(0, 20).map((log) => (
                          <div 
                            key={log.id}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              log.is_active ? 'bg-green-50 border border-green-100' : 'bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* Status icon */}
                              {log.is_active ? (
                                <div className="p-2 bg-green-100 rounded-full">
                                  <Wifi className="w-4 h-4 text-green-600" />
                                </div>
                              ) : (
                                <div className="p-2 bg-slate-100 rounded-full">
                                  <LogOut className="w-4 h-4 text-slate-400" />
                                </div>
                              )}
                              
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{log.username}</span>
                                  <Badge variant="outline" className={ROLE_COLORS[log.role] || ''}>
                                    {ROLE_LABELS[log.role] || log.role}
                                  </Badge>
                                </div>
                                
                                {/* Device info row */}
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                  {log.device_type && (
                                    <span className="flex items-center gap-1">
                                      <DeviceIcon type={log.device_type} />
                                      {log.browser}/{log.os}
                                    </span>
                                  )}
                                  {log.ip_address && (
                                    <span className="font-mono">{log.ip_address}</span>
                                  )}
                                  {log.location?.city && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {log.location.city}, {log.location.country_code}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="text-right text-sm">
                              <div className="flex items-center gap-1 text-slate-600">
                                <LogIn className="w-3 h-3" />
                                {log.login_at ? format(parseISO(log.login_at), 'dd/MM HH:mm', { locale: it }) : '-'}
                              </div>
                              {log.is_active ? (
                                <span className="text-green-600 font-medium text-xs">
                                  Online ({formatDuration(log.duration_minutes)})
                                </span>
                              ) : log.logout_at && (
                                <div className="flex items-center gap-1 text-slate-400 text-xs">
                                  <LogOut className="w-3 h-3" />
                                  {format(parseISO(log.logout_at), 'HH:mm', { locale: it })}
                                  {log.duration_minutes && ` (${formatDuration(log.duration_minutes)})`}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {structure.logs.length > 20 && (
                          <p className="text-center text-sm text-slate-400 py-2">
                            Mostrati 20 di {structure.logs.length} accessi
                          </p>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccessLogsPage;
